import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import StatusBadge from '../../components/StatusBadge';
import accountApi from '../../services/accountApi';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';
import { PLATFORM_WORKER_MANAGE } from '../../auth/permissions';

const date = (value) => value ? new Date(value).toLocaleString() : 'Never';
const connection = (worker) => !worker.is_active ? 'Disabled' : worker.connected ? 'Connected' : worker.has_connected ? 'Offline' : 'Awaiting connection';

export default function PlatformWorkersPage() {
  const { hasPlatformPermission } = usePlatformPermissions();
  const canManage = hasPlatformPermission(PLATFORM_WORKER_MANAGE);
  const [workers, setWorkers] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [setupInfo, setSetupInfo] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enrollment, setEnrollment] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const requestVersion = useRef(0);
  const limit = 25;

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    try {
      const data = await accountApi.listPlatformWorkers({ status: filter, limit, offset });
      if (version !== requestVersion.current) return;
      setWorkers(data.results || []);
      setCount(data.count || 0);
      setError('');
    } catch (err) {
      if (version === requestVersion.current) setError(err.message || 'Unable to load workers.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [filter, offset]);

  useEffect(() => { load(); return () => { requestVersion.current += 1; }; }, [load]);
  useEffect(() => {
    let active = true;
    accountApi.getWorkerSetup().then((data) => { if (active) setSetupInfo(data); })
      .catch(() => { if (active) setSetupInfo({ ready: false, message: 'Unable to load connection settings. Reload this page to retry.' }); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!canManage) { setEnrollment(null); setCreating(false); setConfirm(null); }
  }, [canManage]);

  const showEnrollment = (data) => {
    setEnrollment(data);
    setCopied(false);
    setCopyError('');
    setSelected(null);
  };
  const create = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const data = await accountApi.createPlatformWorker({ name: name.trim(), description: description.trim() });
      setCreating(false); setName(''); setDescription('');
      showEnrollment(data);
      await load();
    } catch (err) { setError(err.message || 'Unable to create worker.'); }
    finally { setBusy(false); }
  };
  const refreshConnection = async () => {
    setBusy(true);
    try {
      const worker = await accountApi.getPlatformWorker(enrollment.worker.id);
      setEnrollment((previous) => previous ? { ...previous, worker } : null);
      await load();
    } catch (err) { setError(err.message || 'Unable to check connection.'); }
    finally { setBusy(false); }
  };
  const copyEnv = async () => {
    try {
      await navigator.clipboard.writeText(enrollment.setup.env);
      setCopied(true); setCopyError('');
    } catch (_) { setCopyError('Clipboard access is unavailable. Select the configuration below and copy it manually.'); }
  };
  const openDetails = async (worker) => {
    setBusy(true); setError('');
    try { setSelected(await accountApi.getPlatformWorker(worker.id)); }
    catch (err) { setError(err.message || 'Unable to load worker details.'); }
    finally { setBusy(false); }
  };
  const performAction = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const data = await accountApi.platformWorkerAction(confirm.worker.id, confirm.action);
      setConfirm(null);
      if (data.setup) showEnrollment(data);
      else setSelected(null);
      await load();
    } catch (err) { setError(err.message || 'Unable to update worker.'); }
    finally { setBusy(false); }
  };

  return <div>
    <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div><h1 className="product-page-title">Worker network</h1>
        <p className="product-page-subtitle mb-0">Connect and manage Bakeonator’s compute workers.</p></div>
      <div className="d-flex gap-2">
        <Button variant="outline-primary" onClick={load} disabled={loading}>Refresh</Button>
        {canManage && <Button onClick={() => { setCreating(true); setError(''); }} disabled={!setupInfo?.ready || busy}>Create worker</Button>}
      </div>
    </div>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {setupInfo && !setupInfo.ready && <Alert variant="warning">{setupInfo.message}</Alert>}
    <div className="product-card mb-3">
      <h2 className="h6">Connect your first worker</h2>
      <p className="mb-1">Create a worker, copy its configuration into the Droplet’s <code>.env</code> file, then start the container.</p>
      <p className="small text-muted mb-0">New workers use communication-only mode. They report heartbeats without claiming jobs or requiring GPU tools. Refresh to check connection status.</p>
      {setupInfo?.ready && <div className="small mt-2">Platform address: <code>{setupInfo.platform_url}</code></div>}
    </div>
    <div className="product-card">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <span>{count} worker{count === 1 ? '' : 's'}</span>
        <Form.Group controlId="worker-status-filter"><Form.Label className="visually-hidden">Filter by status</Form.Label>
          <Form.Select value={filter} onChange={(e) => { setFilter(e.target.value); setOffset(0); }}>
            <option value="">All statuses</option><option value="idle">Idle</option><option value="assigned">Assigned</option>
            <option value="claiming">Claiming</option><option value="offline">Offline / awaiting connection</option><option value="disabled">Disabled</option>
          </Form.Select></Form.Group>
      </div>
      {loading ? <div className="loading-state"><Spinner size="sm" animation="border" className="me-2" />Loading workers…</div> : workers.length === 0 ?
        <p className="text-muted py-4 mb-0">{filter ? 'No workers match this filter.' : 'No workers yet. Create a worker to connect your first Droplet.'}</p> :
        <Table responsive className="product-table" size="sm"><thead><tr><th>Worker</th><th>Connection</th><th>Activity</th><th>Last heartbeat</th><th>Setup</th></tr></thead>
          <tbody>{workers.map((worker) => <tr key={worker.id}>
            <td><Button variant="link" className="p-0 text-start" disabled={busy} onClick={() => openDetails(worker)}>{worker.name}</Button><div className="small text-muted">{worker.description}</div></td>
            <td><Badge bg={worker.connected ? 'success' : 'secondary'}>{connection(worker)}</Badge></td>
            <td><StatusBadge status={worker.status} /></td><td>{date(worker.last_heartbeat_at)}</td>
            <td>{worker.setup_mode === 'commissioning' ? 'Communication only' : 'Existing runtime'}</td>
          </tr>)}</tbody></Table>}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button size="sm" variant="outline-secondary" disabled={loading || offset === 0} onClick={() => setOffset(Math.max(0, offset-limit))}>Previous</Button>
        <Button size="sm" variant="outline-secondary" disabled={loading || offset+limit >= count} onClick={() => setOffset(offset+limit)}>Next</Button>
      </div>
    </div>

    <Modal show={creating && canManage} onHide={() => { if (!busy) setCreating(false); }}>
      <Modal.Header closeButton><Modal.Title>Create worker</Modal.Title></Modal.Header>
      <Form onSubmit={create}><Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="worker-name" className="mb-3"><Form.Label>Worker name</Form.Label>
          <Form.Control autoFocus required maxLength={120} placeholder="bakeonator-worker-dev-01" value={name} onChange={(e) => setName(e.target.value)} /></Form.Group>
        <Form.Group controlId="worker-description"><Form.Label>Description (optional)</Form.Label>
          <Form.Control as="textarea" rows={2} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} /></Form.Group>
        <p className="small text-muted mt-3 mb-0">Creates one worker identity and a unique token. The configuration will be displayed once.</p>
      </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setCreating(false)}>Cancel</Button>
        <Button type="submit" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create and get configuration'}</Button></Modal.Footer></Form>
    </Modal>

    <Modal size="lg" show={Boolean(enrollment && canManage)} onHide={() => setEnrollment(null)} backdrop="static">
      <Modal.Header closeButton><Modal.Title>Connect {enrollment?.worker.name}</Modal.Title></Modal.Header>
      {enrollment && <><Modal.Body>
        <Alert variant="warning">This configuration contains a secret token and is displayed only once. Copy it before closing. If you lose it, rotate the token to generate a replacement.</Alert>
        <h3 className="h6">1. Save the configuration on your Droplet</h3>
        <p>In <code>/root/bakeonator-worker</code>, open <code>nano .env</code> and paste the entire configuration below. Save, then run <code>chmod 600 .env</code>.</p>
        <Form.Group controlId="worker-env"><Form.Label>Worker .env configuration</Form.Label>
          <Form.Control as="textarea" rows={5} readOnly spellCheck={false} className="font-monospace" value={enrollment.setup.env} onFocus={(e) => e.target.select()} /></Form.Group>
        <Button className="my-2" variant="outline-primary" onClick={copyEnv}>{copied ? 'Copied' : 'Copy .env'}</Button>
        {copyError && <Alert variant="info">{copyError}</Alert>}
        <h3 className="h6 mt-3">2. Test and start the worker</h3>
        <pre className="bg-light text-dark p-3 rounded text-wrap">{enrollment.setup.test_command}{'\n'}{enrollment.setup.start_command}</pre>
        <p className="small text-muted">Recreating the container loads the new token. The platform does not log into your Droplet.</p>
        <h3 className="h6">3. Check the connection</h3>
        <div className="d-flex align-items-center gap-3"><Badge bg={enrollment.worker.connected ? 'success' : 'secondary'}>{connection(enrollment.worker)}</Badge>
          <Button variant="outline-primary" disabled={busy} onClick={refreshConnection}>Check connection</Button></div>
        <p className="small mt-2 mb-0">Last heartbeat: {date(enrollment.worker.last_heartbeat_at)}. Connected confirms communication, not job execution.</p>
      </Modal.Body><Modal.Footer><Button onClick={() => setEnrollment(null)}>Done — clear displayed token</Button></Modal.Footer></>}
    </Modal>

    <Modal show={Boolean(selected)} onHide={() => setSelected(null)} size="lg">
      <Modal.Header closeButton><Modal.Title>{selected?.name}</Modal.Title></Modal.Header>
      {selected && <><Modal.Body><p>{selected.description || 'No description.'}</p>
        <p><strong>{connection(selected)}</strong> · Last heartbeat: {date(selected.last_heartbeat_at)}</p>
        <p className="small text-muted">Worker ID: {selected.id}</p>
        <h3 className="h6">Recent activity</h3>
        {selected.recent_attempts?.length ? <Table responsive size="sm"><thead><tr><th>Job</th><th>Status</th><th>Started</th><th>Finished</th></tr></thead><tbody>
          {selected.recent_attempts.map((attempt) => <tr key={attempt.id}><td><code>{attempt.job_id}</code></td><td>{attempt.status}</td><td>{date(attempt.started_at)}</td><td>{date(attempt.finished_at)}</td></tr>)}
        </tbody></Table> : <p className="text-muted">No job attempts yet. Communication-only workers do not execute jobs.</p>}
      </Modal.Body><Modal.Footer>{canManage && <>
        {selected.is_active && selected.setup_mode === 'commissioning' && <Button variant="outline-primary" onClick={() => { setConfirm({ worker: selected, action: 'rotate-token' }); setSelected(null); }}>Rotate token and get .env</Button>}
        {selected.is_active ? <Button variant="outline-danger" onClick={() => { setConfirm({ worker: selected, action: 'disable' }); setSelected(null); }}>Disable worker</Button> : selected.setup_mode === 'commissioning' && <Button onClick={() => { setConfirm({ worker: selected, action: 'enable' }); setSelected(null); }}>Re-enable with new token</Button>}
      </>}<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button></Modal.Footer></>}
    </Modal>

    <Modal show={Boolean(confirm && canManage)} onHide={() => { if (!busy) setConfirm(null); }}>
      <Modal.Header closeButton><Modal.Title>Update {confirm?.worker.name}</Modal.Title></Modal.Header>
      <Modal.Body>{error && <Alert variant="danger">{error}</Alert>}
        {confirm?.action === 'disable' ? 'Disable this worker? Its token will stop authenticating immediately. An active job may require lease recovery; this does not shut down the Droplet.' : 'Issue a new token? Previous credentials will stop working. Copy the replacement .env and recreate the worker container.'}
      </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button>
        <Button variant={confirm?.action === 'disable' ? 'danger' : 'primary'} disabled={busy} onClick={performAction}>{busy ? 'Updating…' : 'Confirm update'}</Button></Modal.Footer>
    </Modal>
  </div>;
}
